import { motion } from "framer-motion";
import { Home, ShieldCheck, Moon, Phone } from "lucide-react";
import { HOUSING_LABELS, useHousing } from "@/components/site/Housing";

const HousingBadge = () => {
  const [h] = useHousing();
  const s = h && HOUSING_LABELS[h.status];
  if (!s) return null;
  return (
    <div data-testid="housing-availability" className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-full border border-sand/25 px-4 py-2 text-sm">
      <span className="relative flex h-2.5 w-2.5"><span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${s.dot}`} /><span className={`relative h-2.5 w-2.5 rounded-full ${s.dot}`} /></span>
      <span className="font-semibold">{s.label}</span>
      {h.note && <span className="text-sand/80">· {h.note}</span>}
      {h.updated_at && <span className="text-xs text-sand/70">· updated {new Date(h.updated_at).toLocaleDateString()}</span>}
    </div>
  );
};

const POINTS = [
  { icon: Moon, title: "Emergency stays", text: "Short-term, safe accommodation for women and children who need to leave now." },
  { icon: Home, title: "Respite", text: "A break in a calm, culturally safe place to rest, recover and plan next steps." },
  { icon: ShieldCheck, title: "Private & protected", text: "Our housing location is never published. Staff arrange everything with you directly." },
];

export const SafeHousing = ({ onRequest }) => (
  <section data-testid="safe-housing-section" className="bg-sand px-5 pt-20 sm:px-10">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto grid max-w-[1440px] overflow-hidden rounded-[32px] bg-ink text-sand lg:grid-cols-12"
    >
      <div className="relative h-64 lg:col-span-5 lg:h-auto">
        <img src="/img/eucalyptus_bush.jpg" alt="Quiet eucalyptus bushland" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent lg:bg-gradient-to-r" />
      </div>
      <div className="p-8 sm:p-12 lg:col-span-7">
        <p className="eyebrow text-amber">Safe housing · Women &amp; children</p>
        <h2 className="mt-4 font-display text-4xl font-medium leading-[1] tracking-tight sm:text-6xl">A safe roof, <span className="italic text-ochre">tonight</span> or for a while.</h2>
        <HousingBadge />
        <p className="mt-5 max-w-xl text-sand/85">Respite and emergency accommodation for women and their children, with support to stay safe and connected to culture. [Add eligibility, capacity and length-of-stay details.]</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {POINTS.map((p) => (
            <div key={p.title} className="border-t border-sand/20 pt-4">
              <p.icon className="h-5 w-5 text-ochre" />
              <p className="mt-3 font-semibold">{p.title}</p>
              <p className="mt-1 text-sm text-sand/80">{p.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-4">
          <button data-testid="service-request-safe-housing" onClick={() => onRequest("Safe housing")} className="btn-ochre">Request housing privately</button>
          <a href="tel:1800737732" data-testid="housing-1800respect-link" className="btn-line border-sand/40 text-sand hover:bg-sand hover:text-ink"><Phone className="h-4 w-4" /> 1800RESPECT</a>
        </div>
        <p className="mt-5 text-xs text-sand/75">In danger right now? Call 000.</p>
      </div>
    </motion.div>
  </section>
);
